// The Schedule page (F520, decision 128): the calendar you click. The
// aircraft you are a member of as rows, the day's hours in UTC across, a
// bar per slot, the free half hours as boxes; a click books one, the
// aircraft is yours when it opens.

import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { queries, type Slot } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { ScheduleBoard, SlotCard, SlotHistory, SlotPopover, pilotColour, useTicking, type Lane } from "@/components/ScheduleBoard";
import { addMinutes, countdown, dayStartUtc, inWords, localClockOf, utcClock, utcDayLabel } from "@/lib/schedule";

export const Route = createFileRoute("/schedule")({
  validateSearch: (search: Record<string, unknown>): { day: string } => ({
    // The UTC day, YYYY-MM-DD; today when absent or malformed.
    day: typeof search.day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.day) ? search.day : "",
  }),
  component: Schedule,
});

function dayOf(search: string, now: Date): Date {
  if (search) {
    const d = new Date(`${search}T00:00:00Z`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return dayStartUtc(now);
}

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function Schedule() {
  const { day } = Route.useSearch();
  const navigate = useNavigate({ from: "/schedule" });
  const me = useQuery(queries.me());
  const now = useTicking();
  const dayStart = dayOf(day, now);
  const dayEnd = addMinutes(dayStart, 24 * 60);
  const schedule = useQuery(queries.schedule(dayStart.toISOString(), dayEnd.toISOString()));
  const [booking, setBooking] = useState<{ lane: Lane; startsAt: Date } | null>(null);
  const [picked, setPicked] = useState<{ slot: Slot; lane: Lane } | null>(null);
  if (me.isPending) return null;
  if (!me.data) return <p className="muted">Sign in to see the schedule.</p>;
  const meId = me.data.user.id;
  const lanes: Lane[] = (schedule.data?.airframes ?? []).map((a) => ({
    id: a.id,
    reg: a.reg,
    name: a.name,
    manages: a.your_role === "owner" || a.your_role === "captain",
    canBook: a.your_role === "owner" || a.your_role === "captain" || a.your_role === "pilot",
    booking: a.booking ? { holder: { id: a.booking.holder.id, name: a.booking.holder.name, handle: a.booking.holder.handle }, acquired_at: a.booking.acquired_at, flying: a.booking.flying } : null,
    slots: a.slots,
  }));
  // The caller's next slot across the fleet, for the chip under the board.
  const mine = lanes
    .flatMap((l) => l.slots.filter((s) => s.yours && (s.state === "planned" || s.state === "open")).map((s) => ({ slot: s, lane: l })))
    .sort((a, b) => a.slot.starts_at.localeCompare(b.slot.starts_at))[0];
  const today = dayStartUtc(now);
  const isToday = dayStart.getTime() === today.getTime();
  const go = (d: Date) => navigate({ search: { day: isoDay(d) } });
  return (
    <div className="space-y-4" data-testid="schedule-page">
      <header className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 className="page-title">Schedule</h1>
        <p className="muted text-sm">Every time is UTC. Click a free half hour to book it: the aircraft is yours when it opens.</p>
        <span className="ml-auto tnum text-sm" data-testid="schedule-clocks">
          {utcClock(now)} <span className="muted text-xs">UTC</span> <span className="muted text-xs">· {localClockOf(now)} on your clock</span>
        </span>
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <div className="daynav" role="group" aria-label="Day">
          <button type="button" className="btn" onClick={() => go(addMinutes(dayStart, -24 * 60))} data-testid="schedule-prev">
            ‹
          </button>
          <button type="button" className={`btn ${isToday ? "btn-primary" : ""}`} onClick={() => go(today)} data-testid="schedule-today">
            {isToday ? `Today, ${utcDayLabel(dayStart)}` : utcDayLabel(dayStart)}
          </button>
          <button type="button" className="btn" onClick={() => go(addMinutes(dayStart, 24 * 60))} data-testid="schedule-next">
            ›
          </button>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-xs muted" aria-label="Legend">
          <span>
            <i className="legend-swatch" style={{ background: pilotColour(meId, meId) }} /> you
          </span>
          <span>
            <i className="legend-swatch sched-flying" /> flying now
          </span>
          <span>
            <i className="legend-swatch legend-free" /> free, click to book
          </span>
        </div>
      </div>
      <ErrorNote error={schedule.error} />
      {schedule.data ? (
        lanes.length === 0 ? (
          <p className="muted" data-testid="schedule-empty">
            No aircraft yet: the schedule shows the aircraft you are a member of.
          </p>
        ) : (
          <ScheduleBoard
            lanes={lanes}
            dayStart={dayStart}
            now={now}
            meId={meId}
            picked={booking ? { laneId: booking.lane.id, startsAt: booking.startsAt } : null}
            onBook={(lane, startsAt) => {
              setPicked(null);
              setBooking({ lane, startsAt });
            }}
            onPick={(slot, lane) => {
              setBooking(null);
              setPicked({ slot, lane });
            }}
          />
        )
      ) : null}
      {booking ? <SlotPopover lane={booking.lane} startsAt={booking.startsAt} meId={meId} onDone={() => setBooking(null)} onClose={() => setBooking(null)} /> : null}
      {picked ? <SlotCard slot={picked.slot} lane={picked.lane} now={now} onClose={() => setPicked(null)} /> : null}
      {mine ? (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="schedule-mine">
          <button type="button" className="chip-row" onClick={() => setPicked(mine)}>
            <i className="legend-swatch" style={{ background: pilotColour(meId, meId) }} />
            <b>Your slot</b> {mine.lane.reg} · {utcClock(new Date(mine.slot.starts_at))} UTC ·{" "}
            {mine.slot.state === "open" ? `booked for you · ${countdown(new Date(mine.slot.ends_at), now)} to start flying` : `yours when it opens, in ${inWords(new Date(mine.slot.starts_at), now)}`}
          </button>
        </div>
      ) : null}
      <SlotHistory items={lanes.flatMap((l) => l.slots.map((slot) => ({ slot, lane: l })))} meId={meId} />
    </div>
  );
}
